import React, { useEffect, useState } from "react";
import { IOnElementValueClickCBData, LineItemElementProps } from "../types";

export const LineItemPreviewPanel: React.FC<{
	onElementValueClick?: (val: IOnElementValueClickCBData) => void;
	onGroupClicked?: (group: string[]) => void;
	lineItemToProcess: LineItemElementProps[];
	isEditing?: boolean;
	onElementsChange?: (elements: LineItemElementProps[]) => void;
}> = ({ lineItemToProcess, onGroupClicked, onElementValueClick, isEditing, onElementsChange }) => {
	const [selectedSource, setSelectedSource] = useState<{ elementIndex: number; value: string } | null>(null);
	const [showDefaultWarning, setShowDefaultWarning] = useState(false);

	// Reset selection when elements are added or removed — the stored index would be stale.
	useEffect(() => {
		setSelectedSource(null);
	}, [lineItemToProcess.length]);

	const handleValueClick = (elementIndex: number, value: string) => {
		if (!selectedSource) {
			if (value === lineItemToProcess[elementIndex]?.defaultValue) {
				setShowDefaultWarning(true);
				return;
			}
			setShowDefaultWarning(false);
			setSelectedSource({ elementIndex, value });
			return;
		}

		if (selectedSource.elementIndex === elementIndex) {
			if (selectedSource.value === value) {
				setSelectedSource(null);
			} else {
				setSelectedSource({ elementIndex, value });
			}
			return;
		}

		// Different element — toggle dependency
		const newElements = lineItemToProcess.map((el) => ({ ...el }));
		const sourceElement = { ...newElements[selectedSource.elementIndex] };
		const currentDeps = sourceElement.valueDependencies ? { ...sourceElement.valueDependencies } : {};
		const currentValueDeps = currentDeps[selectedSource.value] ? [...currentDeps[selectedSource.value]] : [];

		const depIdx = currentValueDeps.findIndex((d) => d.elementIndex === elementIndex);
		if (depIdx === -1) {
			currentValueDeps.push({ elementIndex, parentValues: [value] });
		} else {
			const dep = { ...currentValueDeps[depIdx], parentValues: [...currentValueDeps[depIdx].parentValues] };
			const valIdx = dep.parentValues.indexOf(value);
			if (valIdx === -1) {
				dep.parentValues.push(value);
			} else {
				dep.parentValues.splice(valIdx, 1);
			}
			if (dep.parentValues.length === 0) {
				currentValueDeps.splice(depIdx, 1);
			} else {
				currentValueDeps[depIdx] = dep;
			}
		}

		if (currentValueDeps.length === 0) {
			delete currentDeps[selectedSource.value];
		} else {
			currentDeps[selectedSource.value] = currentValueDeps;
		}

		if (Object.keys(currentDeps).length === 0) {
			delete sourceElement.valueDependencies;
		} else {
			sourceElement.valueDependencies = currentDeps;
		}

		newElements[selectedSource.elementIndex] = sourceElement;
		onElementsChange?.(newElements);
	};

	const isSelected = (elementIndex: number, value: string) =>
		selectedSource?.elementIndex === elementIndex && selectedSource?.value === value;

	const isDependency = (elementIndex: number, value: string) => {
		if (!selectedSource) return false;
		const deps = lineItemToProcess[selectedSource.elementIndex]?.valueDependencies?.[selectedSource.value];
		return deps?.some((d) => d.elementIndex === elementIndex && d.parentValues.includes(value)) ?? false;
	};

	return (
		<>
			{isEditing && (
				<div className="w-full px-1 pb-2 text-xs text-gray-400 font-['Hanken Grotesk']">
					{showDefaultWarning
					? <span className="text-red-400">Default value cannot be dependent on other variables</span>
					: selectedSource ? (() => {
						const allDeps = lineItemToProcess[selectedSource.elementIndex]?.valueDependencies?.[selectedSource.value];
						const depValues = allDeps?.flatMap((d) => d.parentValues) ?? [];
						return (
							<>
								Selected <span className="font-semibold text-blue-700">{selectedSource.value}</span>
								{" - "}
								{depValues.length > 0
									? <>is dependent on <span className="font-semibold text-orange-500">{depValues.map(v => v === "" ? "(empty)" : v).join(", ")}</span></>
									: "click values in other variables to add dependencies"}
							</>
						);
					})() : "Click a value to select it, then click values in other variables to set dependencies"}
				</div>
			)}
			{lineItemToProcess.map((element, ind) => {
				return (
					<React.Fragment key={ind}>
						<div className="grow shrink p-1 bg-white flex-col justify-start items-start gap-2 flex">
							{element.type === "variable" ? (
								[
									<React.Fragment key={`${ind}-0`}>
										{element.acceptableValues?.map((value, i) => {
											const selected = isEditing && isSelected(ind, value);
											const dependency = isEditing && isDependency(ind, value);
											return (
												<span
													key={i}
													className={`${element.conditional && "bg-green-100"} ${!isEditing && onGroupClicked && "btn"} ${selected ? "ring-2 ring-blue-500 rounded" : ""} ${dependency ? "ring-2 ring-orange-400 rounded" : ""}`}
													onClick={() => {
														if (isEditing) {
															handleValueClick(ind, value);
														} else {
															onGroupClicked && onGroupClicked(element.acceptableValues!);
														}
													}}
												>
													<div className="self-stretch justify-start items-start gap-3 inline-flex no-break">
														<div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
															<div
																className={`self-stretch text-slate-600 text-base ${
																	value === element.defaultValue && "font-bold"
																} font-['Hanken Grotesk'] leading-normal ${!isEditing && onElementValueClick && "btn"} ${isEditing && "cursor-pointer"}`}
																onClick={() => {
																	if (!isEditing) {
																		onElementValueClick &&
																			onElementValueClick({
																				element,
																				value,
																				elementIndex: ind,
																				valueIndex: i,
																			});
																	}
																}}
															>
																{value}
															</div>
															{!isEditing && (() => {
																const deps = element.valueDependencies?.[value];
																const parentValues = deps?.flatMap((d) => d.parentValues) ?? [];
																if (!parentValues.length) return null;
																return (
																	<div className="text-[16px] text-orange-400 font-['Hanken Grotesk'] leading-tight">
																		↳ when: {parentValues.map(v => v === "" ? "(empty)" : v).join(", ")}
																	</div>
																);
															})()}
														</div>
													</div>
												</span>
											);
										})}
									</React.Fragment>,
									element.allowEmptyValue ? (
										<u key={`${ind}-1`} className="text-gray-400">
											<span
												className={`text-gray-200 no-break ${(!isEditing && onElementValueClick) || (isEditing && !!selectedSource) ? "btn" : ""} ${isEditing && isDependency(ind, "") ? "ring-2 ring-orange-400 rounded" : ""}`}
												onClick={() => {
													if (isEditing) {
														handleValueClick(ind, "");
													} else {
														onElementValueClick &&
															onElementValueClick({
																element,
																value: "",
																elementIndex: ind,
																valueIndex: -1,
															});
													}
												}}
											>
												empty value
											</span>
										</u>
									) : null,
								]
							) : (
								<>
									<div className="self-stretch justify-start items-start gap-3 inline-flex">
										<div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
											<div className="self-stretch text-gray-400 text-base font-normal font-['Hanken Grotesk'] leading-normal no-break">
												{element.value}
											</div>
										</div>
									</div>
								</>
							)}
						</div>
						{ind !== lineItemToProcess.length - 1 && <img src="/assets/ui/divider.svg" className="w-6 h-32 relative" />}
					</React.Fragment>
				);
			})}
		</>
	);
};
